// diagramTemplates — Style-specifika block-templates som diagramFromPrompt
// använder för att producera meningsfulla DEFAULT-namn istället för
// "Node 1, Node 2, ...". Pure functions: input → templated blocks.
//
// Mönster per style (DiagramPlan.style):
//
// architecture: Entry → Router → Auth → Service → DB (5-lagers)
// pipeline:     Trigger → Validate → Transform → Persist → Notify
// layered:      UI → API → Service → DB
// hub-spoke:    Hub i center, Service-noder runt
// module-graph: Module 1, Module 2, ... (filer fyller in vid runtime)
// swimlane:     Lane 1, Lane 2, ...
// state-machine: Initial → Active → Done (med condition-block emellan)
//
// Sheet-struktur följer style:en så att blocks-listan både har bra namn
// OCH meningsfulla connector-typer (general/condition/webhook).

import type { BlockType } from '@/types/workflow';
import type { DiagramPlan } from '@/lib/aiDiagramTools';

export interface BlockTemplate {
  /** Client-ref som connector-edges använder. */
  ref: string;
  type: BlockType;
  name: string;
  description?: string;
  icon?: string;
}

/**
 * Returnera block-templates för ett sheet givet pos i pipelinen +
 * total style. ~5-8 templates per sheet beroende på style.
 */
export function blocksForSheet(
  sheetIndex: number,
  style: DiagramPlan['style'],
  sheetName: string,
): BlockTemplate[] {
  switch (style) {
    case 'architecture':
      return architectureBlocks(sheetIndex, sheetName);
    case 'pipeline':
      return pipelineBlocks();
    case 'layered':
      return layeredBlocks(sheetIndex);
    case 'hub-spoke':
      return hubSpokeBlocks(sheetIndex === 0 ? 6 : 4);
    case 'swimlane':
      return swimlaneBlocks(sheetIndex);
    case 'module-graph':
      return moduleGraphBlocks(sheetIndex);
  }
}

// ── Arkitektur-templates ──────────────────────────────────────
const ARCHITECTURE_LAYERS: Array<BlockTemplate[]> = [
  // Sheet 0: Entry / Frontend
  [
    { ref: 'entry', type: 'webhook', name: 'Entry', description: 'Application bootstrap', icon: 'Play' },
    { ref: 'router', type: 'condition', name: 'Router', description: 'Route resolution', icon: 'GitBranch' },
    { ref: 'view', type: 'general', name: 'View shell', description: 'UI mount + theme', icon: 'LayoutDashboard' },
    { ref: 'auth-ctx', type: 'general', name: 'Auth context', description: 'Session + user state', icon: 'Lock' },
    { ref: 'i18n', type: 'general', name: 'i18n', description: 'Translations + locale', icon: 'Languages' },
  ],
  // Sheet 1: Application logic
  [
    { ref: 'controller', type: 'general', name: 'Controllers', description: 'Request handlers', icon: 'Wrench' },
    { ref: 'validate', type: 'condition', name: 'Validation', description: 'Input/schema check', icon: 'CheckSquare' },
    { ref: 'service', type: 'general', name: 'Services', description: 'Business logic', icon: 'Settings' },
    { ref: 'event', type: 'webhook', name: 'Events', description: 'Domain events', icon: 'Radio' },
  ],
  // Sheet 2: Data layer
  [
    { ref: 'cache', type: 'general', name: 'Cache', description: 'In-memory store', icon: 'Zap' },
    { ref: 'db', type: 'webhook', name: 'Database', description: 'Persistent storage', icon: 'Database' },
    { ref: 'rls', type: 'condition', name: 'RLS', description: 'Row-level security', icon: 'Shield' },
    { ref: 'audit', type: 'general', name: 'Audit log', description: 'Mutation history', icon: 'FileText' },
  ],
  // Sheet 3+: External / Integrations
  [
    { ref: 'webhook-in', type: 'webhook', name: 'Webhook in', description: 'External callbacks', icon: 'Radio' },
    { ref: 'queue', type: 'general', name: 'Queue', description: 'Async task buffer', icon: 'Package' },
    { ref: 'notify', type: 'send-email', name: 'Notify', description: 'Email + push', icon: 'Bell' },
    { ref: 'monitor', type: 'general', name: 'Monitoring', description: 'Logs + metrics', icon: 'Activity' },
  ],
];

function architectureBlocks(sheetIndex: number, sheetName: string): BlockTemplate[] {
  const layer = ARCHITECTURE_LAYERS[Math.min(sheetIndex, ARCHITECTURE_LAYERS.length - 1)];
  // Stamp:a sheet-name som första blocks description om generic.
  return layer.map((b, i) => i === 0 ? { ...b, description: `${sheetName} — ${b.description ?? ''}`.trim() } : b);
}

// ── Pipeline-template ───────────────────────────────────────────
function pipelineBlocks(): BlockTemplate[] {
  return [
    { ref: 'trigger', type: 'webhook', name: 'Trigger', description: 'External event', icon: 'Play' },
    { ref: 'validate', type: 'condition', name: 'Validate', description: 'Schema + auth', icon: 'CheckSquare' },
    { ref: 'transform', type: 'general', name: 'Transform', description: 'Map + enrich', icon: 'Shuffle' },
    { ref: 'persist', type: 'webhook', name: 'Persist', description: 'Save to store', icon: 'Database' },
    { ref: 'notify', type: 'send-email', name: 'Notify', description: 'Confirm + alert', icon: 'Bell' },
  ];
}

// ── Layered-template ────────────────────────────────────────────
const LAYERED_TIERS: Array<BlockTemplate[]> = [
  [
    { ref: 'ui-comp', type: 'general', name: 'UI components', description: 'Presentation', icon: 'LayoutDashboard' },
    { ref: 'state', type: 'general', name: 'State', description: 'Local + global', icon: 'RefreshCw' },
    { ref: 'router', type: 'condition', name: 'Router', description: 'Navigation', icon: 'GitBranch' },
  ],
  [
    { ref: 'api', type: 'webhook', name: 'API layer', description: 'HTTP endpoints', icon: 'Server' },
    { ref: 'controller', type: 'general', name: 'Controllers', description: 'Request mapping', icon: 'Wrench' },
    { ref: 'middleware', type: 'condition', name: 'Middleware', description: 'Auth + validate', icon: 'Filter' },
  ],
  [
    { ref: 'svc', type: 'general', name: 'Services', description: 'Business logic', icon: 'Settings' },
    { ref: 'domain', type: 'general', name: 'Domain', description: 'Entities + rules', icon: 'Box' },
    { ref: 'event', type: 'webhook', name: 'Events', description: 'Domain events', icon: 'Radio' },
  ],
  [
    { ref: 'repo', type: 'general', name: 'Repositories', description: 'DB access', icon: 'Database' },
    { ref: 'cache', type: 'general', name: 'Cache', description: 'Hot data', icon: 'Zap' },
    { ref: 'audit', type: 'general', name: 'Audit', description: 'Mutation log', icon: 'FileText' },
  ],
];

function layeredBlocks(sheetIndex: number): BlockTemplate[] {
  return LAYERED_TIERS[Math.min(sheetIndex, LAYERED_TIERS.length - 1)];
}

// ── Hub-and-spoke ───────────────────────────────────────────────
function hubSpokeBlocks(spokeCount: number): BlockTemplate[] {
  const hub: BlockTemplate = {
    ref: 'hub', type: 'general', name: 'Hub', description: 'Central node', icon: 'Box',
  };
  const spokes: BlockTemplate[] = Array.from({ length: spokeCount }, (_, i) => ({
    ref: `spoke-${i + 1}`, type: 'general',
    name: `Service ${i + 1}`, description: 'Connected service', icon: 'Settings',
  }));
  return [hub, ...spokes];
}

// ── Swimlane ───────────────────────────────────────────────────
function swimlaneBlocks(sheetIndex: number): BlockTemplate[] {
  const role = ['Customer', 'Sales', 'Support', 'Engineering', 'Finance'][sheetIndex] ?? `Lane ${sheetIndex + 1}`;
  return [
    { ref: 'start', type: 'webhook', name: `${role} request`, description: 'Lane entry', icon: 'Play' },
    { ref: 'check', type: 'condition', name: 'Decision', description: 'Branch logic', icon: 'GitBranch' },
    { ref: 'do', type: 'general', name: 'Action', description: `${role} task`, icon: 'Wrench' },
    { ref: 'handoff', type: 'send-email', name: 'Handoff', description: 'Notify next lane', icon: 'Bell' },
  ];
}

// ── Module-graph ───────────────────────────────────────────────
function moduleGraphBlocks(sheetIndex: number): BlockTemplate[] {
  const mod = ['Core', 'UI', 'API', 'Data', 'Utils'][sheetIndex] ?? `Module ${sheetIndex + 1}`;
  return [
    { ref: 'idx', type: 'general', name: `${mod} index`, description: 'Public exports', icon: 'FileText' },
    { ref: 'types', type: 'general', name: `${mod} types`, description: 'Type definitions', icon: 'Box' },
    { ref: 'impl-1', type: 'general', name: `${mod} core`, description: 'Main implementation', icon: 'Settings' },
    { ref: 'impl-2', type: 'general', name: `${mod} helpers`, description: 'Support fns', icon: 'Wrench' },
    { ref: 'tests', type: 'condition', name: `${mod} tests`, description: 'Unit tests', icon: 'CheckSquare' },
  ];
}

/**
 * Bygg connector-edges baserat på style. Default: linjär kedja
 * (block N → block N+1). Hub-spoke gör hub→spokes.
 */
export function edgesForStyle(
  style: DiagramPlan['style'],
  blocks: BlockTemplate[],
): Array<{ fromRef: string; toRef: string; label?: string }> {
  if (style === 'hub-spoke' && blocks.length > 0) {
    const [hub, ...spokes] = blocks;
    return spokes.map((s) => ({ fromRef: hub.ref, toRef: s.ref }));
  }
  return blocks.slice(0, -1).map((b, i) => ({
    fromRef: b.ref,
    toRef: blocks[i + 1].ref,
  }));
}
